// Single source of truth for turning a speaker-supplied YouTube/Vimeo URL into an
// embeddable player URL. Used both to render the embed (VideoEmbed) and to validate
// the URL while editing (SpeakerProfileEdit, PreviousEngagementsSection) so "valid"
// at edit time actually means "embeddable" at render time.

const YOUTUBE_ID_REGEX = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
const VIMEO_ID_REGEX = /(?:vimeo\.com\/)(?:.*\/)?([a-zA-Z0-9]+(?:\/[a-zA-Z0-9]+)?)/;

function parseQueryParam(url: string, name: string): string | null {
    try {
        return new URL(url).searchParams.get(name);
    } catch {
        return null;
    }
}

// Accepts YouTube's raw-seconds ("90"), suffixed ("90s"), and compound ("1h2m3s") formats.
function parseTimeToSeconds(raw: string): number | null {
    if (/^\d+s?$/.test(raw)) {
        return parseInt(raw, 10);
    }

    const match = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (!match || (!match[1] && !match[2] && !match[3])) {
        return null;
    }

    const [, hours, minutes, seconds] = match;
    return (parseInt(hours ?? '0', 10) * 3600) + (parseInt(minutes ?? '0', 10) * 60) + parseInt(seconds ?? '0', 10);
}

function youtubeStartSeconds(url: string): number | null {
    const raw = parseQueryParam(url, 't') ?? parseQueryParam(url, 'start');

    return raw ? parseTimeToSeconds(raw) : null;
}

// Vimeo's player takes the start time as a URL fragment (#t=90s), which a plain
// URL/searchParams parse won't see — pull it straight off the raw string instead.
function vimeoStartTime(url: string): string | null {
    const hashMatch = url.match(/#t=([\dhms]+)/);
    if (hashMatch) {
        return hashMatch[1];
    }

    return parseQueryParam(url, 't');
}

export function getVideoEmbedUrl(url: string): string | null {
    const youtubeMatch = url.match(YOUTUBE_ID_REGEX);
    if (youtubeMatch) {
        const start = youtubeStartSeconds(url);

        return `https://www.youtube.com/embed/${youtubeMatch[1]}${start ? `?start=${start}` : ''}`;
    }

    const vimeoMatch = url.match(VIMEO_ID_REGEX);
    if (vimeoMatch) {
        const start = vimeoStartTime(url);

        return `https://player.vimeo.com/video/${vimeoMatch[1]}${start ? `#t=${start}` : ''}`;
    }

    return null;
}

export function isEmbeddableVideoUrl(url: string): boolean {
    return getVideoEmbedUrl(url) !== null;
}
